import { useEffect, type ComponentType } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { is } from 'immutable';

import { userGUIDSelector } from '@/domain/env/envSelector';
import { companyConnectedErp } from '@/domain/erpIntegration/queries/connections.select';
import { useErpConnections } from '@/domain/erpIntegration/queries/connections';
import {
  getJELineColumnsVisibilityAction,
  getJournalSettingsOrderedColsAction,
  getJournalSettingsPinnedColsAction,
} from '@/domain/journal/actions';
import { generateJETableStoreKey } from '@/domain/journal/helper';
import {
  initialHiddenColumnsSelector,
  jeGridLineColumnsDictionarySelector,
  jeInvoiceTypeSelector,
} from '@/domain/journal/selectors';

type JEColumnLayoutProps = {
  // Column widths are stored under this key even without a connected ERP.
  tableStoreKey: string;
  // Pinned, hidden and ordered columns are stored only for a connected ERP.
  layoutStoreKey: string | null;
};

export const withJEColumnLayout = <P extends object>(Table: ComponentType<P & JEColumnLayoutProps>) => {
  const WithJEColumnLayout = (props: P) => {
    const { companyId } = useParams();
    const { data: erp = null } = useErpConnections(companyId ?? null, { select: companyConnectedErp });

    const userGUID = useSelector(userGUIDSelector);
    const jeInvoiceType = useSelector(jeInvoiceTypeSelector);
    // Every journal entry answer builds a new dictionary; `is` keeps the previous one while the
    // columns are the same, so the layout is re-read only when they change.
    const columnsDictionary = useSelector(jeGridLineColumnsDictionarySelector, is);
    const initialHiddenColumns = useSelector(initialHiddenColumnsSelector, is);

    const tableStoreKey: string = generateJETableStoreKey([userGUID, jeInvoiceType, erp]);
    const layoutStoreKey = userGUID && jeInvoiceType && erp ? tableStoreKey : null;

    const dispatch = useDispatch();

    useEffect(() => {
      if (layoutStoreKey === null) return;
      dispatch(getJournalSettingsPinnedColsAction({ storeKey: layoutStoreKey }));
      dispatch(getJELineColumnsVisibilityAction({ storeKey: layoutStoreKey }));
      dispatch(getJournalSettingsOrderedColsAction({ storeKey: layoutStoreKey }));
    }, [dispatch, layoutStoreKey, columnsDictionary, initialHiddenColumns]);

    return <Table {...props} tableStoreKey={tableStoreKey} layoutStoreKey={layoutStoreKey} />;
  };

  return WithJEColumnLayout;
};
